/**
 * Login page with OAuth provider selection (Microsoft).
 * Includes dev login bypass with email input for development environments.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

/**
 * A configured user offered by the dev-only picker, projected by
 * GET /auth/dev/users (no secrets — identity plus account/team scope only).
 */
interface DevUserOption {
	id: string;
	email: string;
	name: string | null;
	role: string;
	accounts: { name: string; roleName: string | null }[];
	teams: { name: string; roleName: string | null }[];
}

/**
 * Build the muted secondary line for a picker option: the user's account and
 * team names, so an operator can tell identities apart.
 * @param user - the configured user option
 * @returns the "accounts / teams" summary, or null when the user has neither
 */
function formatScope(user: DevUserOption): string | null {
	const accounts = user.accounts.map((a) => a.name).join(", ");
	const teams = user.teams.map((t) => t.name).join(", ");
	const segments = [accounts, teams].filter(Boolean);
	return segments.length > 0 ? segments.join(" / ") : null;
}

/**
 * Login page
 *
 * Supports the following authentication providers:
 * - Microsoft Azure AD
 * - Dev bypass (development only) - uses browser redirect to simulate OAuth flow
 *
 * Google sign-in is no longer offered: the button is gone but the backend
 * plumbing ("google" provider) is retained so it can be restored if needed.
 */
export function Login() {
	const [devEmail, setDevEmail] = useState("");
	const [devUsers, setDevUsers] = useState<DevUserOption[]>([]);
	const [selectedDevEmail, setSelectedDevEmail] = useState("");

	const isDev = import.meta.env.DEV;

	// Load the configured users so an operator can pick one to sign in as. Dev
	// only; a failed/empty fetch simply leaves the picker in its empty state.
	useEffect(() => {
		if (!isDev) {
			return;
		}
		const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
		fetch(`${apiUrl}/auth/dev/users`)
			.then((res) => (res.ok ? (res.json() as Promise<DevUserOption[]>) : []))
			.then((users) => { setDevUsers(users); })
			.catch(() => { setDevUsers([]); });
	}, [isDev]);

	/**
	 * Initiates OAuth login by redirecting to the backend OAuth endpoint
	 * @param provider - the OAuth provider to use
	 */
	const handleOAuthLogin = (provider: "google" | "microsoft") => {
		// Redirect to backend OAuth endpoint (API is on a different domain in production)
		const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
		window.location.href = `${apiUrl}/auth/${provider}`;
	};

	/**
	 * Initiates dev login by redirecting to the dev auth endpoint
	 * @param email - optional email for dev login, defaults to super admin
	 */
	const handleDevLogin = (email?: string) => {
		// Use browser redirect (not tRPC) to simulate OAuth flow
		// This ensures sessionStorage persists across the redirect, matching real OAuth behavior

		// If no email specified (Super Admin login), clear any pending invite token
		// Super Admin login should bypass invite processing entirely
		if (!email) {
			sessionStorage.removeItem("pendingInviteToken");
		}

		const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
		const emailParam = email ? `?email=${encodeURIComponent(email)}` : "";
		window.location.href = `${apiUrl}/auth/dev${emailParam}`;
	};

	return (
		<div
			className="flex min-h-screen items-center justify-center"
			style={{
				backgroundImage:
					"url('/images/00_rpa_teams_bkgd.jpg')",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<div className="w-full max-w-md space-y-8 rounded-lg border border-sidebar-border bg-sidebar p-8 shadow-2xl drop-shadow-2xl">
				{/* Logo */}
				<div className="text-center">
					<img
						src="/rpai-logo.png"
						alt="RPAi"
						className="mx-auto"
						
					/>
					<h1 className="mt-4 text-2xl font-bold text-sidebar-foreground">Welcome to RPAi</h1>
					<p className="mt-2 text-sm text-sidebar-muted">
						Sign in to access your marketing dashboard
					</p>
				</div>

				{/* OAuth Providers */}
				<div className="space-y-3">
					<Button
						variant="outline"
						className="w-full"
						onClick={() => { handleOAuthLogin("microsoft"); }}
					>
						<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
							<path fill="#f25022" d="M1 1h10v10H1z" />
							<path fill="#00a4ef" d="M1 13h10v10H1z" />
							<path fill="#7fba00" d="M13 1h10v10H13z" />
							<path fill="#ffb900" d="M13 13h10v10H13z" />
						</svg>
						Sign in with Microsoft
					</Button>
				</div>

				{/* Dev Bypass (development only) */}
				{isDev && (
					<>
						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-sidebar px-2 text-sidebar-muted">
									Development Only
								</span>
							</div>
						</div>

						<Button
							variant="secondary"
							className="w-full"
							onClick={() => { handleDevLogin(); }}
						>
							Dev Login (Super Admin)
						</Button>

						{/* Picker: sign in as any configured user to test their access */}
						<div className="space-y-2">
							<p className="text-xs uppercase tracking-wide text-sidebar-muted">
								Log in as configured user
							</p>
							<Select
								value={selectedDevEmail}
								onValueChange={setSelectedDevEmail}
								disabled={devUsers.length === 0}
							>
								<SelectTrigger className="bg-background text-foreground">
									<SelectValue
										placeholder={
											devUsers.length === 0 ? "No configured users available" : "Select a user…"
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{devUsers.map((user) => {
										const scope = formatScope(user);
										return (
											<SelectItem key={user.id} value={user.email}>
												<span className="flex flex-col">
													<span>
														{user.name ?? user.email} ({user.role})
													</span>
													{scope ? (
														<span className="text-xs text-muted-foreground">{scope}</span>
													) : null}
												</span>
											</SelectItem>
										);
									})}
								</SelectContent>
							</Select>
							<Button
								variant="outline"
								className="w-full"
								onClick={() => { handleDevLogin(selectedDevEmail); }}
								disabled={!selectedDevEmail}
							>
								Log in as selected user
							</Button>
						</div>

						{/* Email input for testing invites */}
						<div className="flex gap-2">
							<Input
								type="email"
								placeholder="test@example.com"
								value={devEmail}
								onChange={(e) => { setDevEmail(e.target.value); }}
								onKeyDown={(e) => {
									if (e.key === "Enter" && devEmail) {
										e.preventDefault();
										handleDevLogin(devEmail);
									}
								}}
								className="flex-1 bg-background text-foreground"
							/>
							<Button
								variant="outline"
								onClick={() => { handleDevLogin(devEmail || undefined); }}
								disabled={!devEmail}
							>
								Login as Email
							</Button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
